import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Circle,
  Lock,
  Clock,
  Star,
  ArrowLeft,
  Loader2,
  WifiOff,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCabinModule } from '../hooks/cabin/useCabinModule';

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

function getLessonIcon(status) {
  if (status === 'completed') return <CheckCircle size={18} className="text-[#10B981] shrink-0" aria-hidden="true" />;
  if (status === 'locked') return <Lock size={18} className="text-gray-400 shrink-0" aria-hidden="true" />;
  return <Circle size={18} className="text-[var(--text-muted)] shrink-0" aria-hidden="true" />;
}

function LessonRow({ lesson, lang, t, onLessonClick }) {
  const isLocked = lesson.status === 'locked';
  const title =
    lang === 'tr'
      ? (lesson.title?.tr || lesson.title_tr || lesson.title || '')
      : (lesson.title?.en || lesson.title_en || lesson.title?.tr || lesson.title || '');

  return (
    <motion.button
      whileHover={isLocked ? {} : { x: 2 }}
      whileTap={isLocked ? {} : { scale: 0.99 }}
      onClick={() => !isLocked && onLessonClick(lesson)}
      disabled={isLocked}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] ${
        isLocked
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:bg-sky-50 cursor-pointer'
      }`}
      aria-label={`${title}${isLocked ? ` — ${t('lesson.locked') || 'kilitli'}` : ''}`}
      aria-disabled={isLocked}
      data-testid={`lesson-row-${lesson.lesson_id || lesson.code}`}
    >
      {getLessonIcon(lesson.status)}

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold truncate ${
          lesson.status === 'completed' ? 'text-[var(--text-muted)] line-through' : 'text-[var(--text-main)]'
        }`}>
          {title}
        </p>
        {lesson.duration_min && (
          <p className="text-xs text-[var(--text-muted)] flex items-center gap-1 mt-0.5">
            <Clock size={11} aria-hidden="true" />
            {lesson.duration_min} {t('lesson.minutes') || 'dk'}
          </p>
        )}
      </div>

      {lesson.miles_reward > 0 && (
        <span className="flex items-center gap-1 text-xs font-semibold text-amber-500 shrink-0">
          <Star size={12} aria-hidden="true" />
          {lesson.miles_reward}
        </span>
      )}

      {!isLocked && (
        <ChevronRight size={16} className="text-[var(--text-muted)] shrink-0" aria-hidden="true" />
      )}
    </motion.button>
  );
}

function UnitAccordion({ unit, lang, t, onLessonClick }) {
  const [expanded, setExpanded] = useState(true);
  const shouldReduce = useReducedMotion();

  const unitTitle =
    lang === 'tr'
      ? (unit.title?.tr || unit.title_tr || unit.title || '')
      : (unit.title?.en || unit.title_en || unit.title?.tr || unit.title || '');

  const lessons = unit.lessons || [];
  const completedCount = lessons.filter((l) => l.status === 'completed').length;

  return (
    <div
      className="border-2 border-[var(--border-light)] rounded-2xl overflow-hidden bg-white shadow-[0_4px_0_0_var(--border-light)]"
      data-testid={`unit-accordion-${unit.unit_id || unit.code}`}
    >
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
        aria-expanded={expanded}
        aria-controls={`unit-lessons-${unit.unit_id || unit.code}`}
        data-testid={`unit-toggle-${unit.unit_id || unit.code}`}
      >
        <BookOpen size={18} className="text-[var(--brand-primary)] shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[var(--text-main)] truncate">{unitTitle}</p>
          <p className="text-xs text-[var(--text-muted)]">
            {completedCount}/{lessons.length} {t('unit.lessons_done') || 'ders tamamlandı'}
          </p>
        </div>

        {/* Unit progress pill */}
        {lessons.length > 0 && (
          <div className="shrink-0 w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden" aria-hidden="true">
            <div
              className="h-full bg-[#10B981] rounded-full transition-all"
              style={{ width: `${(completedCount / lessons.length) * 100}%` }}
            />
          </div>
        )}

        {expanded ? (
          <ChevronUp size={16} className="text-[var(--text-muted)] shrink-0" aria-hidden="true" />
        ) : (
          <ChevronDown size={16} className="text-[var(--text-muted)] shrink-0" aria-hidden="true" />
        )}
      </button>

      <motion.div
        id={`unit-lessons-${unit.unit_id || unit.code}`}
        initial={false}
        animate={
          shouldReduce
            ? {}
            : { height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }
        }
        style={shouldReduce ? { display: expanded ? 'block' : 'none' } : {}}
        transition={{ duration: 0.2 }}
        className="overflow-hidden"
      >
        <div className="px-2 pb-2 flex flex-col gap-1">
          {lessons.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] px-4 py-3">
              {t('unit.no_lessons') || 'Bu ünitede henüz ders yok.'}
            </p>
          ) : (
            lessons.map((lesson) => (
              <LessonRow
                key={lesson.lesson_id || lesson.code}
                lesson={lesson}
                lang={lang}
                t={t}
                onLessonClick={onLessonClick}
              />
            ))
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default function CabinModuleDetail() {
  const { moduleSlug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const shouldReduce = useReducedMotion();
  const { data: module, isLoading, isError } = useCabinModule(moduleSlug);

  const handleLessonClick = (lesson) => {
    const id = lesson.lesson_id || lesson.code;
    navigate(`/programs/kabin-memurlugu/lessons/${id}`, {
      state: { moduleSlug },
    });
  };

  const handleBack = () => {
    navigate('/programs/kabin-memurlugu/modules');
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]"
        data-testid="module-detail-loading"
        role="status"
        aria-label={t('common.loading') || 'Yükleniyor...'}
      >
        <Loader2 size={32} className="text-[var(--brand-primary)] animate-spin" aria-hidden="true" />
      </div>
    );
  }

  if (isError || !module) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[var(--bg-app)] px-4"
        data-testid="module-detail-error"
      >
        <div className="flex flex-col items-center gap-4 text-center max-w-sm">
          <WifiOff size={48} className="text-[var(--brand-danger)]" aria-hidden="true" />
          <p className="text-lg font-bold text-[var(--text-main)]">
            {t('cabin.error.title') || 'Bağlantı koptu'}
          </p>
          <p className="text-sm text-[var(--text-muted)]">
            {t('cabin.error.sub') || 'Veriler yüklenemedi. Tekrar dene.'}
          </p>
          <button
            onClick={handleBack}
            className="px-6 py-2.5 bg-[var(--brand-primary)] text-white font-bold rounded-xl"
          >
            {t('common.go_back') || 'Geri Dön'}
          </button>
        </div>
      </div>
    );
  }

  const moduleTitle =
    lang === 'tr'
      ? (module.title?.tr || module.title_tr || module.title || '')
      : (module.title?.en || module.title_en || module.title?.tr || module.title || '');

  const moduleDesc =
    lang === 'tr'
      ? (module.description?.tr || module.description_tr || '')
      : (module.description?.en || module.description_en || module.description?.tr || '');

  const units = module.units || [];
  const gradient = module.color_gradient || module.gradient || 'from-blue-500 to-indigo-600';

  return (
    <motion.div
      variants={shouldReduce ? {} : pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="min-h-screen bg-[var(--bg-app)]"
      data-testid="cabin-module-detail"
    >
      {/* Header */}
      <div className={`bg-gradient-to-br ${gradient} px-4 pt-4 pb-10 sm:px-6 lg:px-8`}>
        <div className="max-w-3xl mx-auto">
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 text-white/80 hover:text-white mb-4 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white rounded-lg text-sm font-semibold"
            data-testid="module-detail-back"
          >
            <ArrowLeft size={16} aria-hidden="true" />
            {t('common.back') || 'Geri'}
          </button>

          <div className="flex items-center gap-3">
            <span className="text-4xl" aria-hidden="true">
              {module.icon_emoji || module.emoji || '📚'}
            </span>
            <div>
              <h1 className="text-2xl font-black text-white">{moduleTitle}</h1>
              {moduleDesc && (
                <p className="text-sm text-white/80 mt-1">{moduleDesc}</p>
              )}
            </div>
          </div>

          {/* Meta badges */}
          <div className="flex flex-wrap gap-2 mt-4">
            {(module.total_units || module.unitCount) > 0 && (
              <span className="px-3 py-1 bg-white/20 text-white text-xs font-bold rounded-lg">
                {module.total_units || module.unitCount} {t('module.units') || 'ünite'}
              </span>
            )}
            {(module.total_lessons || module.lessonCount) > 0 && (
              <span className="px-3 py-1 bg-white/20 text-white text-xs font-bold rounded-lg">
                {module.total_lessons || module.lessonCount} {t('module.lessons') || 'ders'}
              </span>
            )}
            {(module.estimated_hours || module.estimatedTime) && (
              <span className="px-3 py-1 bg-white/20 text-white text-xs font-bold rounded-lg flex items-center gap-1">
                <Clock size={11} aria-hidden="true" />
                {module.estimated_hours
                  ? `${module.estimated_hours} ${t('module.hours') || 'saat'}`
                  : `${module.estimatedTime} ${t('module.hours') || 'saat'}`}
              </span>
            )}
            {(module.miles_reward || module.milesReward) > 0 && (
              <span className="px-3 py-1 bg-white/20 text-white text-xs font-bold rounded-lg flex items-center gap-1">
                <Star size={11} aria-hidden="true" />
                {module.miles_reward || module.milesReward} {t('module.miles') || 'Mil'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Units list */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 pb-12">
        {units.length === 0 ? (
          <div
            className="bg-white rounded-2xl border-2 border-[var(--border-light)] p-8 text-center"
            data-testid="module-detail-empty"
          >
            <BookOpen size={40} className="text-[var(--text-muted)] mx-auto mb-3" aria-hidden="true" />
            <p className="font-bold text-[var(--text-main)] mb-1">
              {t('module.no_units_title') || 'Henüz ünite yok'}
            </p>
            <p className="text-sm text-[var(--text-muted)]">
              {t('module.no_units_desc') || 'Bu modül için ünite ve dersler yakında eklenecek.'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {units.map((unit) => (
              <UnitAccordion
                key={unit.unit_id || unit.code}
                unit={unit}
                lang={lang}
                t={t}
                onLessonClick={handleLessonClick}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
